import {
    Button,
    Icon,
    InitialsAvatar,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@elit-hair/elit-ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbArrowDown, TbArrowUp, TbArrowsSort, TbChartBar, TbList, TbSettings } from "react-icons/tb";
import z from "zod";

import { useTeams } from "@/hooks/queries/team/use-team-queries";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { cn } from "@/lib/utils";
import { AgentSettingsDialog } from "@/pages/management/office/components/agent-settings-dialog";
import { useUserStatusContext } from "@/providers/general/user-status.provider";
import { UserStatus } from "@/types/user-statuses.types";
import type { IUser } from "@/types/user.types";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";
import { getStatusCircleColor } from "@/utils/user-status";

const FILTER_ALL = "all";
const HIDDEN_TEAM_NAME_PREFIX = "_";

const SORT_COLUMNS = ["agent", "email", "team", "status"] as const;
const SORT_DIRECTIONS = ["asc", "desc"] as const;

type SortColumn = (typeof SORT_COLUMNS)[number];
type SortDirection = (typeof SORT_DIRECTIONS)[number];

type SortState = {
    column: SortColumn;
    direction: SortDirection;
};

const ManagementUsersSearchSchema = z.object({
    q: z.string().optional(),
    team: z.string().optional(),
    status: z.string().optional(),
    sort: z.enum(SORT_COLUMNS).optional(),
    dir: z.enum(SORT_DIRECTIONS).optional(),
});

type ManagementUsersSearch = z.infer<typeof ManagementUsersSearchSchema>;

export const Route = createFileRoute(APP_ROUTES_FULL.MANAGEMENT_USERS)({
    component: ManagementUsers,
    validateSearch: zodValidator(ManagementUsersSearchSchema),
    staticData: {
        config: {
            breadcrumb: {
                title: "users",
                to: APP_ROUTES_TO.MANAGEMENT_USERS,
            },
            title: "users",
        },
    },
});

function isUserStatus(value: string): value is UserStatus {
    return Object.values(UserStatus).some((status) => status === value);
}

type UserTableRow = {
    user: IUser;
    status: UserStatus;
    teamIds: string[];
    teamNames: string[];
};

type SortableTableHeadProps = {
    label: string;
    column: SortColumn;
    sortState: SortState;
    onSort: (column: SortColumn) => void;
};

function SortableTableHead({ label, column, sortState, onSort }: SortableTableHeadProps) {
    const isActive = sortState.column === column;

    return (
        <TableHead>
            <button
                type="button"
                onClick={() => onSort(column)}
                className={cn(
                    "inline-flex items-center gap-1 rounded-sm transition-colors",
                    isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                )}
            >
                <span>{label}</span>
                {isActive ? (
                    <Icon icon={sortState.direction === "asc" ? TbArrowUp : TbArrowDown} size={14} />
                ) : (
                    <Icon icon={TbArrowsSort} size={14} className="opacity-50" />
                )}
            </button>
        </TableHead>
    );
}

function compareRows(a: UserTableRow, b: UserTableRow, column: SortColumn): number {
    switch (column) {
        case "agent":
            return a.user.name.localeCompare(b.user.name);
        case "email":
            return a.user.email.localeCompare(b.user.email);
        case "team":
            return a.teamNames
                .slice()
                .sort((left, right) => left.localeCompare(right))
                .join(", ")
                .localeCompare(
                    b.teamNames
                        .slice()
                        .sort((left, right) => left.localeCompare(right))
                        .join(", ")
                );
        case "status":
            return a.status.localeCompare(b.status);
    }
}

function ManagementUsers() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const routeNavigate = Route.useNavigate();
    const search = Route.useSearch({ structuralSharing: true });

    const [searchInput, setSearchInput] = useState(search.q ?? "");
    const debouncedSearchInput = useDebouncedValue(searchInput);
    const [settingsUserId, setSettingsUserId] = useState<string | null>(null);

    const teamFilter = search.team ?? FILTER_ALL;
    const statusFilter = search.status && isUserStatus(search.status) ? search.status : FILTER_ALL;
    const sortState: SortState = {
        column: search.sort ?? "agent",
        direction: search.dir ?? "asc",
    };

    const { data: teamsResponse, isPending } = useTeams();
    const teams = useMemo(
        () => (teamsResponse?.data ?? []).filter((team) => !team.name.startsWith(HIDDEN_TEAM_NAME_PREFIX)),
        [teamsResponse?.data]
    );
    const isInitialLoading = isPending && !teamsResponse;
    const { userStatuses } = useUserStatusContext();

    useEffect(() => {
        setSearchInput(search.q ?? "");
    }, [search.q]);

    useEffect(() => {
        const nextQ = debouncedSearchInput.trim() || undefined;
        if ((search.q || undefined) === nextQ) {
            return;
        }

        routeNavigate({
            to: Route.to,
            search: (prev: ManagementUsersSearch) => ({
                ...prev,
                q: nextQ,
            }),
            replace: true,
        });
    }, [debouncedSearchInput, routeNavigate, search.q]);

    const updateSearch = (updates: Partial<ManagementUsersSearch>) => {
        routeNavigate({
            to: Route.to,
            search: (prev: ManagementUsersSearch) => ({
                ...prev,
                ...updates,
            }),
            replace: true,
        });
    };

    const teamOptions = useMemo(
        () => [...teams].map((team) => ({ id: team.id, name: team.name })).sort((a, b) => a.name.localeCompare(b.name)),
        [teams]
    );

    const teamUsers = useMemo(() => {
        const byUserId = new Map<string, UserTableRow>();

        for (const team of teams) {
            for (const member of team.members) {
                const user = member.user;
                if (!user?.id) {
                    continue;
                }

                const existing = byUserId.get(user.id);
                if (existing) {
                    if (!existing.teamIds.includes(team.id)) {
                        existing.teamIds.push(team.id);
                        existing.teamNames.push(team.name);
                    }
                    continue;
                }

                byUserId.set(user.id, {
                    user,
                    status: userStatuses[user.id] ?? UserStatus.OFFLINE,
                    teamIds: [team.id],
                    teamNames: [team.name],
                });
            }
        }

        return Array.from(byUserId.values());
    }, [teams, userStatuses]);

    const rows = useMemo(() => {
        const query = (search.q ?? "").trim().toLowerCase();
        const directionFactor = sortState.direction === "asc" ? 1 : -1;

        return teamUsers
            .map((row) => ({
                ...row,
                status: userStatuses[row.user.id] ?? UserStatus.OFFLINE,
            }))
            .filter((row) => {
                if (statusFilter !== FILTER_ALL && row.status !== statusFilter) {
                    return false;
                }

                if (teamFilter !== FILTER_ALL && !row.teamIds.includes(teamFilter)) {
                    return false;
                }

                if (!query) {
                    return true;
                }

                const name = row.user.name.toLowerCase();
                const email = row.user.email.toLowerCase();
                const teamLabel = row.teamNames.join(" ").toLowerCase();
                return name.includes(query) || email.includes(query) || teamLabel.includes(query);
            })
            .sort((a, b) => compareRows(a, b, sortState.column) * directionFactor);
    }, [teamUsers, userStatuses, search.q, statusFilter, teamFilter, sortState]);

    const handleSort = (column: SortColumn) => {
        if (sortState.column === column) {
            updateSearch({
                sort: column,
                dir: sortState.direction === "asc" ? "desc" : "asc",
            });
            return;
        }

        updateSearch({ sort: column, dir: "asc" });
    };

    const handleGoToCalls = (userId: string) => {
        navigate({
            to: APP_ROUTES_TO.CALLS,
            search: (old) => ({ ...old, agent: userId }),
        });
    };

    const handleGoToKpis = (userId: string) => {
        navigate({
            to: APP_ROUTES_TO.PERFORMANCE_KPIS,
            search: { agent: userId },
        });
    };

    return (
        <div className="flex h-full min-h-0 flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3">
                <Input
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder={t("search_users")}
                    className="max-w-sm"
                />
                <Select
                    value={teamFilter}
                    onValueChange={(value) =>
                        updateSearch({
                            team: value === FILTER_ALL ? undefined : value,
                        })
                    }
                >
                    <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder={t("team")} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={FILTER_ALL}>{t("all")}</SelectItem>
                        {teamOptions.map((team) => (
                            <SelectItem key={team.id} value={team.id}>
                                {team.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select
                    value={statusFilter}
                    onValueChange={(value) =>
                        updateSearch({
                            status: value === FILTER_ALL ? undefined : value,
                        })
                    }
                >
                    <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder={t("status")} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={FILTER_ALL}>{t("all")}</SelectItem>
                        {Object.values(UserStatus).map((status) => (
                            <SelectItem key={status} value={status}>
                                {t(`user_status.${status}`)}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <SortableTableHead
                                label={t("agent")}
                                column="agent"
                                sortState={sortState}
                                onSort={handleSort}
                            />
                            <SortableTableHead
                                label={t("email")}
                                column="email"
                                sortState={sortState}
                                onSort={handleSort}
                            />
                            <SortableTableHead
                                label={t("team")}
                                column="team"
                                sortState={sortState}
                                onSort={handleSort}
                            />
                            <SortableTableHead
                                label={t("status")}
                                column="status"
                                sortState={sortState}
                                onSort={handleSort}
                            />
                            <TableHead className="text-right">{t("actions")}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isInitialLoading ? (
                            <TableRow>
                                <TableCell className="h-32 text-center">
                                    <div className="flex justify-center py-8">
                                        <Spinner size="sm" />
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : rows.length === 0 ? (
                            <TableRow>
                                <TableCell className="h-32 text-center text-muted-foreground">
                                    {t("no_users_found")}
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((row) => (
                                <TableRow key={row.user.id}>
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            <InitialsAvatar name={row.user.name} size="sm" />
                                            <span className="font-medium">{row.user.name}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">{row.user.email}</TableCell>
                                    <TableCell>
                                        <span className="text-sm">
                                            {[...row.teamNames].sort((a, b) => a.localeCompare(b)).join(", ")}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <span
                                                className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${getStatusCircleColor(row.status)}`}
                                            />
                                            <span className="text-sm capitalize">{t(`user_status.${row.status}`)}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center justify-end gap-1">
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        aria-label={t("go_to_calls")}
                                                        onClick={() => handleGoToCalls(row.user.id)}
                                                    >
                                                        <Icon icon={TbList} />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>{t("go_to_calls")}</TooltipContent>
                                            </Tooltip>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        aria-label={t("go_to_kpis")}
                                                        onClick={() => handleGoToKpis(row.user.id)}
                                                    >
                                                        <Icon icon={TbChartBar} />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>{t("go_to_kpis")}</TooltipContent>
                                            </Tooltip>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        aria-label={t("open_user_settings")}
                                                        onClick={() => setSettingsUserId(row.user.id)}
                                                    >
                                                        <Icon icon={TbSettings} />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>{t("open_user_settings")}</TooltipContent>
                                            </Tooltip>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            <AgentSettingsDialog
                open={settingsUserId !== null}
                onOpenChange={(open) => {
                    if (!open) setSettingsUserId(null);
                }}
                userId={settingsUserId ?? undefined}
            />
        </div>
    );
}
